<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied/>
  </div>
  <div v-else>
    <div class="m-3 mb-2">
      <h5 class="mb-1">{{ $t('Category Management') }}</h5>
      <p class="text-muted mb-0" style="font-size: 13px;">
        {{ $t('Drag to reorder how categories appear on the menu.') }}
      </p>
    </div>

    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row">
      <div class="btn-group btn-group-sm me-sm-2 mb-3">
        <button
          type="button"
          class="btn btn-sm btn-primary"
          @click="() => {
            offcanvasFroms.open = true;
            offcanvasFroms.type = 'CREATE';
          }"
        >
          <i class="fa-solid fa-circle-plus me-2" />
          {{ $t('Create') }}
        </button>
      </div>

      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button class="input-group-text" type="button" @click="fetchData">
          <i class="fas fa-search" />
        </button>
      </div>

      <!-- สถานะการบันทึกลำดับ — ลากแล้วบันทึกอัตโนมัติ ไม่มีปุ่ม Save ให้ลืมกด -->
      <div class="d-flex align-items-center mb-3 small" :class="sortState.error ? 'text-danger' : 'text-muted'">
        <template v-if="sortState.saving">
          <i class="fa-solid fa-spinner fa-spin me-1" />{{ $t('Saving order') }}...
        </template>
        <template v-else-if="sortState.error">
          <i class="fa-solid fa-triangle-exclamation me-1" />{{ $t('Could not save the new order') }}
        </template>
        <template v-else-if="sortState.saved">
          <i class="fa-solid fa-circle-check me-1 text-success" />{{ $t('Order saved') }}
        </template>
      </div>
    </div>

    <div class="mx-3 mb-3">
      <div v-if="loading" class="text-center text-secondary py-5">
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </div>
      <div v-else-if="items.length === 0" class="text-center text-secondary py-5 border rounded">
        <i class="fa-regular fa-folder-open fa-2x mb-2" /><br>
        <span class="small">{{ $t('No Available Data') }}</span>
      </div>
      <draggable
        v-else
        v-model="items"
        item-key="id"
        handle=".drag-handle"
        :animation="200"
        ghost-class="drag-ghost"
        :disabled="!!params.search"
        @end="persistOrder"
      >
        <template #item="{ element: category, index }">
          <div class="card mb-2 border shadow-sm">
            <div class="card-body d-flex align-items-center gap-2 py-2 px-2">
              <span
                class="drag-handle d-inline-flex align-items-center justify-content-center"
                :class="params.search ? 'invisible' : ''"
                style="width: 22px; height: 22px; font-size: 12px; cursor: grab; touch-action: none; flex-shrink: 0;"
                :title="$t('Drag to reorder')"
              >
                <i class="fa-solid fa-grip-vertical" />
              </span>
              <span
                class="badge rounded-pill bg-light text-secondary border flex-shrink-0"
                style="min-width: 30px;"
              >
                {{ index + 1 }}
              </span>

              <div class="flex-grow-1" style="min-width: 0;">
                <div class="fw-semibold text-truncate">{{ $ln(category) }}</div>
              </div>

              <span class="badge bg-primary flex-shrink-0">
                {{ category.products_count + ' ' + $t('Items') }}
              </span>
              <span
                v-if="!category.is_active"
                class="badge bg-secondary flex-shrink-0"
              >
                {{ $t('Inactive') }}
              </span>

              <div class="icon-link custom-actions flex-shrink-0">
                <popper :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm btn-icon"
                    @click="() => {
                      offcanvasFroms.open = true;
                      offcanvasFroms.id = category.id;
                      offcanvasFroms.type = 'EDIT';
                    }"
                  >
                    <i class="fa-solid fa-square-pen" />
                  </button>
                  <template #content><div>{{ $t('Edit') }}</div></template>
                </popper>
                <popper :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm btn-icon"
                    :disabled="category.products_count > 0"
                    @click="handleDelete(category)"
                  >
                    <i class="fa-solid fa-trash-can" />
                  </button>
                  <!-- ลบหมวดที่ยังมีสินค้า = สินค้ากลายเป็นกำพร้า BE บล็อกไว้ ปุ่มจึงปิดตามให้เห็นเหตุผลตั้งแต่แรก -->
                  <template #content>
                    <div>{{ category.products_count > 0 ? $t('Move its products to another category first') : $t('Delete') }}</div>
                  </template>
                </popper>
              </div>
            </div>
          </div>
        </template>
      </draggable>

      <!-- ค้นหาอยู่ = เห็นไม่ครบทุกแถว ลากแล้วลำดับจะเพี้ยน จึงล็อกการลากไว้ -->
      <div v-if="params.search && items.length > 0" class="text-muted small mt-2">
        <i class="fa-solid fa-circle-info me-1" />{{ $t('Clear the search to reorder categories.') }}
      </div>
    </div>

    <offcanvas-form
      v-if="offcanvasFroms.open"
      :type="offcanvasFroms.type"
      :id="offcanvasFroms.id"
    />
  </div>
</template>
<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import OffcanvasForm from './OffcanvasForm.vue';
import { ProductCategories } from '@/services/endpoint/product-categories.service';
import draggable from 'vuedraggable';
import Popper from "vue3-popper";

export default {
  data() {
    return {
      // หมวดหมู่มีจำนวนน้อยโดยธรรมชาติ + ต้องลากสลับข้ามแถวได้ทั้งชุด → ดึงมาทีเดียวไม่แบ่งหน้า
      params: {
        limit: 500,
        page: 1,
        search: ''
      },
      pageLoading: false,
      loading: false,
      hasPermission: false,
      items: [],
      sortState: {
        saving: false,
        saved: false,
        error: false
      },
      offcanvasFroms: {
        open: false,
        type: 'CREATE',
        id: 0
      }
    }
  },
  components: {
    PlaceholderContent,
    AccessDenied,
    OffcanvasForm,
    draggable,
    Popper
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    /** ชื่ออีกภาษาที่ไม่ใช่ภาษาที่กำลังแสดง — ว่างได้ ไม่ถือเป็นข้อผิดพลาด */
    secondaryName(item) {
      return this.$i18n.locale === 'en' ? item.name : (item.name_en || '');
    },
    async fetchData() {
      this.loading = true;
      var queryString = '?' + $.param(this.params);
      await ProductCategories.list(queryString).then(res => {
        this.items = res.data.data;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
      });
      this.loading = false;
    },
    /**
     * ลากเสร็จ = บันทึกทันที ส่งลำดับใหม่ทั้งชุด (index ใน array = priority)
     * ล้มเหลว → โหลดใหม่เพื่อกลับไปตรงกับ DB ไม่ปล่อยให้หน้าจอโชว์ลำดับที่ยังไม่ได้บันทึก
     */
    async persistOrder() {
      this.sortState = { saving: true, saved: false, error: false };
      try {
        await ProductCategories.sort({ ids: this.items.map(c => c.id) });
        this.sortState = { saving: false, saved: true, error: false };
        setTimeout(() => { this.sortState.saved = false; }, 2000);
      } catch (err) {
        this.sortState = { saving: false, saved: false, error: true };
        this.$toast.warn(err?.response?.data?.message || this.$t('Could not save the new order'));
        await this.fetchData();
      }
    },
    async handleDelete(item) {
      if (item.products_count > 0) return;
      try {
        let self = this;
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: this.$t("Delete this Category") + '?',
          html: this.$t("You cannot undo this action!"),
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm"),
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return ProductCategories.delete(item.id)
              .then((response) => {
                self.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Success!'));
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(self.$t(error?.response?.data?.message))
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(self.$t(error?.response?.data?.message))
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
  }
}
</script>

<style scoped>
.drag-handle {
  color: #c4c9cf;
  transition: color 0.15s ease;
  user-select: none;
}
.drag-handle:hover {
  color: #E96C15;
}
.drag-handle:active {
  cursor: grabbing !important;
}
.drag-ghost {
  opacity: 0.5;
}
.sortable-chosen .drag-handle {
  color: #E96C15;
}
</style>
